import React, { useCallback, useMemo } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    ConnectionProvider,
    IntegrationConnection,
} from "@common/types/integrationConnection";
import { INTEGRATION_CONNECTIONS_SETTINGS } from "@common/helpers/integrationConnection";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { Collapsible } from "@design-system/Collapsible/Collapsible";
import { timeAgo } from "@helpers/date";
import { AlertIcon, ClockIcon } from "@icons";
import { DisplayIntegrationAccount } from "../../../integrationAccount/DisplayIntegrationAccount";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { CONNECTION_PROVIDER_INSTITUTIONS } from "@components/institution/resolveInstitutionIcon";
import { NewIntegrationConnection } from "../../../integrationConnection/NewIntegrationConnection";
import css from "./ProviderIntegrationConnections.module.scss";

interface Props {
    integrationConnections: IntegrationConnection[];
    provider: ConnectionProvider;
}

export const MultiAccountConnections: React.FC<Props> = ({
    integrationConnections,
    provider,
}) => {
    const settings = INTEGRATION_CONNECTIONS_SETTINGS[provider];

    const mostRecentBalanceSyncedAt = useCallback(
        (integrationConnection: IntegrationConnection) => {
            if (!integrationConnection.integrationAccounts?.length) {
                return null;
            }

            const dates = integrationConnection.integrationAccounts
                .map((account) => account.balanceSyncedAt)
                .filter((date): date is Date => date instanceof Date);

            if (!dates.length) {
                return null;
            }

            return timeAgo(
                dates.reduce(
                    (latest, current) =>
                        current.getTime() > latest.getTime() ? current : latest,
                    dates[0],
                ),
            );
        },
        [],
    );

    const footer = useMemo(() => {
        if (settings.transactions?.canAddNewAccountUnderConnection) {
            // Not implemented yet
            return null;
        }

        if (settings.transactions?.multipleAccountsPerConnection) {
            return null;
        }

        return (
            <div className={css.footerSection}>
                <NewIntegrationConnection
                    provider={provider}
                    btnVariant="primary"
                >
                    Add another account
                </NewIntegrationConnection>
            </div>
        );
    }, [
        settings.transactions?.multipleAccountsPerConnection,
        settings.transactions?.canAddNewAccountUnderConnection,
        provider,
    ]);

    return integrationConnections?.map((connection) => (
        <Collapsible
            className={css.container}
            key={connection.id}
            header={
                <div className={css.header}>
                    <InstitutionIcon
                        institution={CONNECTION_PROVIDER_INSTITUTIONS[provider]}
                        size="lg"
                    />
                    <main>
                        <h5>
                            {settings.label}
                            {connection.error && (
                                <Tooltip content="One or more accounts are disconnected.">
                                    <span>
                                        <AlertIcon className="ml-2 icon-color-red-500" />
                                    </span>
                                </Tooltip>
                            )}
                        </h5>
                        {connection && mostRecentBalanceSyncedAt && (
                            <small>
                                <ClockIcon
                                    height={12}
                                    width={12}
                                    className="icon-color-current"
                                />
                                {mostRecentBalanceSyncedAt(connection)}
                            </small>
                        )}
                    </main>
                </div>
            }
            footer={footer}
        >
            {connection.integrationAccounts?.map((account) => (
                <DisplayIntegrationAccount
                    key={account.id}
                    className={css.displayAccount}
                    connection={connection}
                    integrationAccount={account}
                    noFooter={
                        !settings.transactions?.canAddNewAccountUnderConnection
                    }
                />
            ))}
        </Collapsible>
    ));
};
